import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, ShieldCheck, ExternalLink, Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { toast } from "sonner";

export default function Payment() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null); // null=loading, obj=ok, false=error
  const [cfgError, setCfgError] = useState("");
  const [copied, setCopied] = useState("");
  const [txHash, setTxHash] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [verified, setVerified] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/payment/config/${token}`)
      .then((r) => {
        setCfg(r.data);
        if (r.data.already_paid) {
          // Already paid — jump straight to password setup
          navigate(`/accept-invitation/${token}`, { replace: true });
        }
      })
      .catch((e) => {
        setCfg(false);
        setCfgError(formatApiError(e));
      });
  }, [token, navigate]);

  const copy = async (val, label) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/payment/verify", { token, tx_hash: txHash.trim() });
      setVerified(data);
      toast.success("Payment confirmed on-chain");
      // Self-signup users are auto-activated by the backend — log them straight
      // in and drop them into the dashboard instead of asking for a password.
      if (data.auto_login) {
        localStorage.setItem("em_os_token", data.auto_login.token);
        sessionStorage.removeItem("emos_signup_pending");
        setTimeout(() => window.location.assign("/"), 1400);
        return;
      }
      setTimeout(() => navigate(`/accept-invitation/${token}`, { replace: true }), 1600);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (cfg === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-0 text-zinc-500 text-sm tracking-widest uppercase">
        Loading payment
      </div>
    );
  }

  // Invalid invitation
  if (cfg === false) {
    return (
      <div className="min-h-screen bg-ink-0 text-white flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-red-400 mb-3">Invitation invalid</p>
          <h2 className="font-display text-3xl font-light tracking-tight mb-4">
            Link expired<span className="text-gold">.</span>
          </h2>
          <p className="text-sm text-zinc-500 leading-relaxed mb-8">{cfgError}</p>
          <button onClick={() => navigate("/login")} className="text-sm text-gold hover:text-gold-hover">Go to sign in</button>
        </div>
      </div>
    );
  }

  // Plain address in QR — maximum wallet compatibility (Trust, MetaMask,
  // Coinbase, Binance, exchange apps). EIP-681 transfer URIs work only in
  // a subset of wallets and confuse the rest. User picks USDT + enters amount
  // manually in their sending flow.
  const uri = cfg.wallet_address;

  return (
    <div className="min-h-screen bg-ink-0 text-white relative">
      <div className="absolute top-6 right-6 z-10">
        <LanguageSwitcher />
      </div>

      <div className="max-w-2xl mx-auto px-6 pt-16 pb-24">
        <div className="flex items-baseline gap-2 mb-12">
          <span className="font-display text-2xl font-semibold">EM</span>
          <span className="font-display text-2xl font-light text-gold">Operating System</span>
        </div>

        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3" data-testid="payment-eyebrow">Step 1 of 2 — Activation payment</p>
        <h1 className="font-display text-4xl font-light tracking-tight mb-3">
          Welcome, <span className="text-gold">{cfg.name?.split(" ")[0] || "Leader"}</span>.
        </h1>
        <p className="text-sm text-zinc-400 leading-relaxed mb-10 max-w-md">
          Your access is one payment away. Send <span className="text-white font-semibold">{cfg.amount_usdt} USDT</span> on the <span className="text-white font-semibold">Ethereum (ERC-20)</span> network to the address below — this activates your account for <span className="text-white font-semibold">one full year</span>. Once confirmed, you&rsquo;ll set your password and step inside.
        </p>

        {verified ? (
          <div className="em-card p-8 text-center" data-testid="payment-verified">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 mx-auto mb-4 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-emerald-400" />
            </div>
            <h2 className="font-display text-2xl font-light tracking-tight mb-2">
              Payment confirmed<span className="text-gold">.</span>
            </h2>
            <p className="text-sm text-zinc-400 mb-1">{verified.amount_usdt} USDT received</p>
            <p className="text-xs text-zinc-600 mb-6">Redirecting you to set your password&hellip;</p>
            <Loader2 className="w-5 h-5 animate-spin text-gold mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* QR + address card */}
            <div className="em-card p-6">
              <div className="bg-white rounded-2xl p-5 mb-4 flex items-center justify-center">
                <QRCodeSVG value={uri} size={200} level="M" includeMargin={false} data-testid="payment-qr" />
              </div>

              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Amount</div>
                <div className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2.5">
                  <span data-testid="payment-amount" className="font-mono text-lg text-white">{cfg.amount_usdt} USDT</span>
                  <button
                    type="button"
                    onClick={() => copy(cfg.amount_usdt, "amount")}
                    className="text-zinc-500 hover:text-gold"
                    aria-label="Copy amount"
                  >
                    {copied === "amount" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Wallet address · ERC-20</div>
                <div className="bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <code data-testid="payment-address" className="text-xs text-white font-mono break-all leading-relaxed">{cfg.wallet_address}</code>
                    <button
                      type="button"
                      onClick={() => copy(cfg.wallet_address, "address")}
                      className="shrink-0 text-zinc-500 hover:text-gold mt-0.5"
                      data-testid="payment-copy-address"
                      aria-label="Copy address"
                    >
                      {copied === "address" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-start gap-2 text-[11px] text-amber-200/70 leading-relaxed">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-400" />
                <span>Only send <strong className="text-amber-200">USDT on the Ethereum (ERC-20) network</strong>. Other assets, tokens or networks (BSC, Polygon, TRON) will be lost.</span>
              </div>
            </div>

            {/* Verify TX card */}
            <div className="em-card p-6 flex flex-col">
              <h3 className="font-display text-xl font-light tracking-tight mb-2">Confirm your payment</h3>
              <p className="text-xs text-zinc-500 leading-relaxed mb-5">
                After sending, paste your <strong className="text-zinc-300">transaction hash</strong> below. We verify it on-chain and unlock your activation.
              </p>

              <form onSubmit={submit} className="space-y-4 flex-1 flex flex-col" data-testid="payment-form">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">Transaction hash</label>
                  <input
                    data-testid="payment-tx-input"
                    value={txHash}
                    onChange={(e) => setTxHash(e.target.value)}
                    placeholder="0x… — 66-character Ethereum transaction hash"
                    className="em-input font-mono text-xs"
                    required
                    minLength={64}
                    maxLength={68}
                    autoComplete="off"
                  />
                  <a
                    href={txHash.length >= 60 ? `https://etherscan.io/tx/${txHash.trim().startsWith("0x") ? txHash.trim() : "0x" + txHash.trim()}` : "https://etherscan.io"}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-[11px] text-zinc-500 hover:text-gold"
                  >
                    <ExternalLink className="w-3 h-3" /> View on Etherscan
                  </a>
                </div>

                {err && (
                  <div className="text-xs text-red-400 bg-red-900/20 border border-red-900/40 rounded-lg px-3 py-2" data-testid="payment-error">
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || txHash.trim().length < 60}
                  data-testid="payment-verify-btn"
                  className="w-full bg-gold text-ink-0 font-semibold rounded-lg px-6 py-3 hover:bg-gold-hover disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  <span>{submitting ? "Verifying on-chain…" : "Verify payment"}</span>
                </button>

                <p className="text-[11px] text-zinc-600 leading-relaxed mt-auto">
                  Ethereum needs about 12 confirmations (~3 minutes). If verification fails with &ldquo;waiting for confirmations&rdquo;, wait a moment and try again — your transaction hash stays valid.
                </p>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
